import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { TAvailabilitiesFormValues } from "../../../models/Onboarding/onboarding";
import { Days } from "../../../models/Onboarding/onboarding-values";
import { useAppSelector, useAppDispatch as useDispatch } from "../../../store";
import { showNotificationAction } from "../../../store/application/slice";
import { availabilitiesThunk } from "../../../store/onboarding/thunk";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import CustomButton from "../../UI/Buttons/CustomButton";
import PlusCircle from "../../UI/Icons/PlusCircle";
import RoundedTick from "../../UI/Icons/RoundedTick";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import "./Availabilities.scss";

type TimeSlot = { start_time: string; end_time: string; error?: string };
type TimeSlots = Record<Days, TimeSlot[]>;

const allDays: Days[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  // "Saturday", - Hidden weekend
  // "Sunday", - Hidden weekend
];

export const generateFullHourOptions = () => {
  const times: { key_param: string; value_param: string }[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hour24 = hour.toString().padStart(2, "0");
      const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
      const period = hour < 12 ? "am" : "pm";
      const minuteStr = minute.toString().padStart(2, "0");

      times.push({
        key_param: `${hour24}:${minuteStr}`,
        value_param: `${formattedHour}:${minuteStr}${period}`,
      });

      if (hour === 23 && minute === 30) {
        return times;
      }
    }
  }

  return times;
};

interface AvailabilitiesProps {
  setShowAvailabilitiesModal: Dispatch<SetStateAction<boolean>>;
  checkedState?: {
    [weekKey: string]: {
      [dayName: string]: { date: string; checked: boolean };
    };
  };
  loadCalendarData?: () => void; // Optional function to reload calendar data
  isFromMyPreference?: boolean;
}

const Availabilities: FC<AvailabilitiesProps> = ({
  setShowAvailabilitiesModal,
  checkedState,
  loadCalendarData,
  isFromMyPreference = false,
}) => {
  const dispatch = useDispatch();

  const inProgress = useAppSelector((state) => state.onboarding.inProgress);
  const availabilitiesFromProfile = useAppSelector(
    (state) => state.onboarding.availabilities
  );
  const [duplicateSlotError, setDuplicateSlotError] = useState<any>([]);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const everydayDefaultSlot = [{ start_time: "07:00", end_time: "18:00" }];

  const timeOptions = generateFullHourOptions();
  const [selectedDays, setSelectedDays] = useState<Days[]>([]);

  const [timeSlots, setTimeSlots] = useState<TimeSlots>(() => {
    const initialSlots: TimeSlots = {
      Everyday: [],
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
      Sunday: [],
    };
    return initialSlots;
  });

  /** Change and select the days */
  const handleDayChange = (day: Days) => {
    if (day === "Everyday") {
      setSelectedDays((prev) =>
        prev?.includes("Everyday") ? [] : ["Everyday", ...allDays]
      );
    } else {
      setSelectedDays((prev) =>
        prev?.includes(day)
          ? prev.filter((d) => d !== day && d !== "Everyday")
          : [...(prev || []), day]
      );
    }

    setTimeSlots((prev) => {
      const updatedSlots = { ...prev };
      if (day === "Everyday") {
        if (selectedDays.includes("Everyday")) {
          updatedSlots["Everyday"] = [];
          allDays.forEach((d) => {
            updatedSlots[d] = [];
          });
        } else {
          updatedSlots["Everyday"] = everydayDefaultSlot;
          allDays.forEach((d) => {
            updatedSlots[d] = everydayDefaultSlot;
          });
        }
      } else {
        if (selectedDays.includes(day)) {
          updatedSlots[day] = [];
        } else {
          updatedSlots[day] = everydayDefaultSlot;
        }
      }
      return updatedSlots;
    });
  };

  useEffect(() => {
    // Filter out timeSlots for days not in selectedDays
    if (selectedDays.length > 0) {
      setTimeSlots((prev) => {
        const updatedSlots = Object.keys(prev)
          .filter((day) => selectedDays.includes(day as Days))
          .reduce((acc, day) => {
            acc[day as Days] = prev[day as Days];
            return acc;
          }, {} as Record<Days, { start_time: string; end_time: string; error?: string }[]>);

        return updatedSlots;
      });
    }
  }, [selectedDays]);

  const hasRun = useRef(false);

  // useEffect(() => { // Don't delete this code, it is used to set the availabilities from profile
  //   if (!hasRun.current && (availabilitiesFromProfile ?? []).length > 0) {
  //     hasRun.current = true;

  //     const updatedSlots = { ...timeSlots };

  //     availabilitiesFromProfile?.forEach((slot) => {
  //       const day = slot.day_of_week as Days;
  //       const capitalizedDay = capitalize(day);
  //       if (updatedSlots[capitalizedDay as Days]) {
  //         updatedSlots[capitalizedDay as Days].push({
  //           start_time: slot.start_time,
  //           end_time: slot.end_time,
  //         });
  //       } else {
  //         updatedSlots[capitalizedDay as Days] = [
  //           { start_time: slot.start_time, end_time: slot.end_time },
  //         ];
  //       }
  //       setSelectedDays((prev) => {
  //         if (!prev.includes(capitalizedDay as Days)) {
  //           return capitalizedDay ? [...prev, capitalizedDay as Days] : prev;
  //         }
  //         return prev;
  //       });
  //     });
  //     setTimeSlots(updatedSlots);
  //   }
  // }, [timeSlots]);

  const capitalize = (day: string) => {
    if (day) {
      return day.charAt(0).toUpperCase() + day.slice(1);
    }
  };

  const checkTimeRangeOverlap = (slot1: TimeSlot, slot2: TimeSlot) => {
    const start1 = slot1.start_time;
    const end1 = slot1.end_time;
    const start2 = slot2.start_time;
    const end2 = slot2.end_time;

    // Check if ranges overlap
    return start1 <= end2 && start2 <= end1;
  };

  /** Change and update the time */
  const handleTimeChange = (
    day: Days,
    index: number,
    field: "start_time" | "end_time",
    value: string
  ) => {
    const updatedSlots = [...timeSlots[day]];
    updatedSlots[index][field] = value;

    // Check if the updated slot is an exact duplicate
    const isDuplicate = updatedSlots.some((slot, i) => {
      if (i !== index) {
        return (
          slot.start_time === updatedSlots[index].start_time &&
          slot.end_time === updatedSlots[index].end_time
        );
      }
      return false;
    });

    // Check if the updated slot overlaps with any existing slot
    const isOverlapping = updatedSlots.some((slot, i) => {
      if (i !== index) {
        return checkTimeRangeOverlap(updatedSlots[index], slot);
      }
      return false;
    });

    if (isDuplicate) {
      updatedSlots[index].error = "This time slot is already selected.";
    } else if (isOverlapping) {
      updatedSlots[index].error = "Time range overlaps with existing slot.";
    } else {
      delete updatedSlots[index].error;
    }

    setTimeSlots((prev) => ({ ...prev, [day]: updatedSlots }));
  };

  /**
   * Convrting the time slots into an array of objects for easier processing
   * This function converts the time slots object into an array of objects API team just updated the API to accept this format
   * with day, start_time, and end_time properties.
   * @param timeSlots
   * @returns
   */
  const getAvailabilitiesArray = (timeSlots: TimeSlots) => {
    const { Everyday, ...timeSlotsWithoutEveryday } = timeSlots;
    const result: { day: string; start_time: string; end_time: string }[] = [];
    Object.entries(timeSlotsWithoutEveryday).forEach(([day, slots]) => {
      (slots as { start_time: string; end_time: string }[]).forEach((slot) => {
        result.push({
          day,
          start_time: slot.start_time,
          end_time: slot.end_time,
        });
      });
    });
    return result;
  };

  useEffect(() => {
    setFieldValue("availabilities", getAvailabilitiesArray(timeSlots));
    setFieldValue("is_available", 2);
    setFieldValue("type", "popup");
  }, [timeSlots]);

  const prepareAvailabilityInputData = () => {
    setFieldValue("availabilities", getAvailabilitiesArray(timeSlots));
    setFieldValue("is_available", 2);
    setFieldValue("type", "popup");
  };

  /**
   * Filters the end_time options based on the selected start_time.
   */
  const getFilteredEndTimeOptions = (startTime: string) => {
    return timeOptions.filter((option) => option.key_param > startTime);
  };

  /** Add the timeslot based on the users choice */
  const addTimeSlot = (day: Days) => {
    setTimeSlots((prev) => ({
      ...prev,
      [day]: [...prev[day], { start_time: "07:00", end_time: "18:00" }],
    }));
  };

  /** Remove the selected timeslot if the user click that particular remove button */
  const removeTimeSlot = (day: Days, index: number) => {
    const updatedSlots = timeSlots[day].filter((_, i) => i !== index);
    setTimeSlots((prev) => ({ ...prev, [day]: updatedSlots }));
    const clearedErrors = { ...duplicateSlotError };
    Object.keys(clearedErrors).forEach((day) => {
      clearedErrors[day as Days] = [];
    });
    setDuplicateSlotError(clearedErrors);
  };

  /** ✅ Apply "Everyday" time slots to all days */
  const applyToAllDays = () => {
    const everydaySlots = timeSlots["Everyday"];
    setTimeSlots((prev) => {
      const updatedSlots = { ...prev };
      allDays.forEach((day) => {
        updatedSlots[day] = [...everydaySlots]; // Copy time slots to all days
      });
      return updatedSlots;
    });
  };

  /** Submit the form for availabilities */
  const { handleSubmit, values, errors, setFieldValue } =
    useFormik<TAvailabilitiesFormValues>({
      validationSchema: createValidationSchema("availabilities"),
      initialValues: {
        availabilities: [],
        is_available: 2,
        type: "popup",
      },
      onSubmit: async (values: TAvailabilitiesFormValues) => {
        // check is any duplicate time slots are selected
        const updatedErrors: Record<
          Days,
          { start_time?: string; end_time?: string }[]
        > = {
          Everyday: [],
          Monday: [],
          Tuesday: [],
          Wednesday: [],
          Thursday: [],
          Friday: [],
          Saturday: [],
          Sunday: [],
        };

        // Update the onSubmit function
        Object.entries(timeSlots).forEach(([day, slots]) => {
          if (!updatedErrors[day as Days]) {
            updatedErrors[day as Days] = [];
          }

          slots.forEach((slot, index) => {
            // Check for exact duplicates
            const duplicates = slots
              .map((s, i) => ({ ...s, index: i }))
              .filter(
                (s) =>
                  s.start_time === slot.start_time &&
                  s.end_time === slot.end_time
              );

            // Check for overlapping time ranges
            const overlappingSlots = slots
              .map((s, i) => ({ ...s, index: i }))
              .filter((s, i) => i !== index && checkTimeRangeOverlap(slot, s));

            if (duplicates.length > 1) {
              const lastDuplicateIndex =
                duplicates[duplicates.length - 1].index;
              /**
               * Checking duplicate time slots and setting the error message for the last duplicate
               */
              if (index === lastDuplicateIndex) {
                updatedErrors[day as Days][index] = {
                  start_time: "Duplicate time slot",
                  end_time: "Duplicate time slot",
                };
              } else {
                updatedErrors[day as Days][index] = {}; // clear errors for earlier duplicates
              }
            } else if (overlappingSlots.length > 0) {
              // Check for overlapping ranges
              updatedErrors[day as Days][index] = {
                start_time: "Time range overlaps with existing slot",
                end_time: "Time range overlaps with existing slot",
              };
            } else {
              updatedErrors[day as Days][index] = {}; // no duplicate or overlap
            }
          });
        });

        if (
          Object.entries(updatedErrors).some(([day, dayErrors]) =>
            dayErrors.some((error) => error.start_time || error.end_time)
          )
        ) {
          setDuplicateSlotError(updatedErrors);
          return;
        }
        const payload = { ...values };
        // payload.availabilities = values.filter(
        //   (slot) => slot.day !== "Everyday"
        // );
        try {
          const result = await dispatch(availabilitiesThunk(payload));
          if (isFulfilled(result)) {
            setShowAvailabilitiesModal(false);
            dispatch(showNotificationAction("Saved"));
            setTimeout(() => {
              if (loadCalendarData) {
                loadCalendarData();
              }

              if(isFromMyPreference) {
                window.location.reload();
              }
            }, NOTIFICATION_TIME_OUT);
          }
        } catch (error) {
          throw new Error(
            "Could not connect to services at the moment. Please try again. Thanks."
          );
        }
      },
    });

  /** Set the availabilities based on the user chose */
  useEffect(() => {
    setFieldValue("availabilities", timeSlots);
  }, [setFieldValue, timeSlots]);

  /** Check all the values got updated for enabling the next button */
  useEffect(() => {
    // Initially disable the button when the page loads
    setIsDisabled(true);

    if (
      Object.keys(values.availabilities).length > 0 &&
      !errors.availabilities
    ) {
      setIsDisabled(false);
    }
  }, [errors, values]);

  return (
    <>
      <div className="d-flex flex-column gap-20 max-w-650px">
        <div className="fs-30 fw-400 text-black">Availability</div>
        <div className="fs-16 fw-400 text-black">
          Set your availability to work below. Each timeframe can only be a
          minimum of 2 hour blocks.
        </div>
        <div className="d-flex flex-column gap-20 mobile-availability-scroll">
          <div className="d-flex flex-column gap-10">
            {(["Everyday", ...allDays] as Days[]).map((day) => (
              <div
                key={day}
                className={`radius-10px p-10px d-flex flex-column flex-lg-row gap-10 ${
                  selectedDays.includes(day) ? "bg-teal-light" : "bg-off-white"
                }`}
              >
                <div className="theme-check min-w-180px day_select_checkbox">
                  <input
                    type="checkbox"
                    id={day}
                    value={day}
                    checked={selectedDays.includes(day)}
                    onChange={() => handleDayChange(day)}
                    className="theme-check-input"
                  />
                  <label
                    className={`theme-check-label fs-16 fw-700 ${
                      selectedDays.includes(day) ? "" : "text-grey-medium"
                    }`}
                    htmlFor={day}
                  >
                    {day}
                  </label>
                </div>

                {/* Show time slot inputs only if this day is selected */}
                {selectedDays.includes(day) && (
                  <div className="d-flex flex-column gap-10 flex-fill">
                    {day === "Everyday" &&
                      selectedDays.includes("Everyday") && (
                        <div className="fs-13 fw-400 text-black">
                          Set timeframe/s to apply to all days:
                        </div>
                      )}
                    {timeSlots?.[day]?.map((slot, index) => (
                      <>
                        <div
                          key={index}
                          className="d-flex align-items-center gap-10"
                        >
                          <select
                            value={slot.start_time}
                            onChange={(e) =>
                              handleTimeChange(
                                day,
                                index,
                                "start_time",
                                e.target.value
                              )
                            }
                            className="form-select"
                          >
                            {timeOptions.map((key, value) => (
                              <option
                                key={key.key_param}
                                value={key.key_param}
                                selected={
                                  slot.start_time === key.key_param + ":00"
                                }
                              >
                                {key.value_param}
                              </option>
                            ))}
                          </select>

                          <div className="fs-16 fw-400 text-black">-</div>

                          <select
                            value={slot.end_time}
                            onChange={(e) =>
                              handleTimeChange(
                                day,
                                index,
                                "end_time",
                                e.target.value
                              )
                            }
                            className="form-select"
                          >
                            {/* {timeOptions.map((key, value) => (
                              <option
                                key={key.key_param}
                                value={key.key_param}
                                selected={
                                  slot.end_time === key.key_param + ":00"
                                }
                              >
                                {key.value_param}
                              </option>
                            ))} */}

                            {getFilteredEndTimeOptions(slot.start_time).map(
                              (key) => (
                                <option
                                  key={key.key_param}
                                  value={key.key_param}
                                  selected={
                                    slot.end_time === key.key_param + ":00"
                                  }
                                >
                                  {key.value_param}
                                </option>
                              )
                            )}
                          </select>

                          {timeSlots[day].length > 1 && (
                            <svg
                              onClick={() => {
                                removeTimeSlot(day, index);
                              }}
                              className="min-w-24px cursor-pointer"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6.68867 6.31133C7.31703 5.68297 8.33579 5.68297 8.96414 6.31133L12.3773 9.72453L15.7906 6.31133C16.4189 5.68297 17.4377 5.68297 18.066 6.31133C18.6944 6.93968 18.6944 7.95844 18.066 8.5868L14.6528 12L18.066 15.4132C18.6944 16.0416 18.6944 17.0603 18.066 17.6887C17.4377 18.317 16.4189 18.317 15.7906 17.6887L12.3773 14.2755L8.96414 17.6887C8.33579 18.317 7.31703 18.317 6.68867 17.6887C6.06032 17.0603 6.06032 16.0416 6.68867 15.4132L10.1019 12L6.68867 8.5868C6.06032 7.95844 6.06032 6.93968 6.68867 6.31133Z"
                                fill="#000000"
                                stroke="#E0E0E0"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {duplicateSlotError[day]?.[index]?.start_time && (
                          <ErrorMessage
                            message={
                              duplicateSlotError[day][index]
                                .start_time as string
                            }
                          />
                        )}
                        {errors && (
                          <ErrorMessage
                            message={
                              (
                                errors.availabilities as
                                  | Record<Days, { end_time?: string }[]>
                                  | undefined
                              )?.[day]?.[index]?.end_time as any
                            }
                          />
                        )}
                      </>
                    ))}

                    <button
                      onClick={() => addTimeSlot(day)}
                      className="add_timeframe_btn_container btn-theme bg-transparent text-black mobile-w-100 desktop-h-auto fs-13"
                    >
                      <PlusCircle />
                      Add Another Timeframe
                    </button>
                    {/* Show "Apply to All Days" button only when "Everyday" is selected */}
                    {day === "Everyday" &&
                      selectedDays.includes("Everyday") && (
                        <button
                          onClick={applyToAllDays}
                          className="apply_to_all_days_btn_contaiiner btn-theme bg-teal text-black h-30px"
                        >
                          Apply to All Days
                        </button>
                      )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* {selectedDays.length === 0 &&
          availabilitiesFromProfile &&
          availabilitiesFromProfile?.length > 0 ? (
            <ErrorMessage message="Please select atleast one availability." />
          ) : (
            ""
          )} */}
          <div className="d-flex flex-column flex-lg-row gap-10 justify-content-lg-between">
            <div></div>
            <CustomButton
              type="submit"
              isLoading={inProgress}
              contentClass="d-flex"
              variant="primary"
              disabled={isDisabled}
              onClick={() => {
                prepareAvailabilityInputData();
                handleSubmit();
              }}
              className={`btn-theme ${
                isDisabled
                  ? "text-grey-medium bg-off-white"
                  : "bg-teal text-black"
              }`}
            >
              <RoundedTick
                color={isDisabled || inProgress ? "#757575" : "#000000"}
              />
              Save Changes
            </CustomButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default Availabilities;
